import { Clock, Search } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { cn } from '../../lib/cn'

/**
 * **Eine Aufgabe an eine Uhrzeit legen – ohne Ziehen** (Nils, 14.10.2026: *„ich brauche dann auch
 * noch das ich im kalender ggf. hinzufügen drücken kann und dann auch die auswählen kann, wenn drag
 * n drop mal nicht klappt"*).
 *
 * Geöffnet über „+ Einplanen" im Kopf des Rasters oder einen Klick auf eine leere Stelle darin –
 * dann steht die Uhrzeit schon da. Oben die Gruppen, aus denen man meistens wählt (Markiertes,
 * Gefährdetes, Zum Einplanen), darunter durchsucht das Feld den ganzen offenen Bestand. Auch der
 * Weg für das Telefon, wo nicht gezogen wird (Z-20) – und für die Tastatur (Leitplanke 3).
 */

export type EinplanenZeile = { id: string; title: string; hinweis?: string | undefined }
export type EinplanenGruppe = { titel: string; zeilen: readonly EinplanenZeile[] }

const UHR = (h: number) =>
  `${String(Math.floor(h)).padStart(2, '0')}:${String(Math.round((h % 1) * 60)).padStart(2, '0')}`

/** `"14:15"` → 855 Minuten; `null`, wenn das Feld leer oder unlesbar ist. */
function minutenAus(feld: string): number | null {
  const t = /^(\d{1,2}):(\d{2})$/.exec(feld)
  if (!t) return null
  const m = Number(t[1]) * 60 + Number(t[2])
  return m >= 0 && m < 1440 ? m : null
}

export function EinplanenDialog({
  tagName,
  start,
  gruppen,
  bestand,
  einplanen,
  schliessen,
}: {
  /** „Heute", „Morgen", „Do, 16. Okt." – steht in der Überschrift. */
  tagName: string
  /** Vorbelegte Uhrzeit in Stunden. */
  start: number
  gruppen: readonly EinplanenGruppe[]
  /** Alles Offene – für die Suche. */
  bestand: readonly EinplanenZeile[]
  einplanen: (taskId: string, minuten: number) => void
  schliessen: () => void
}) {
  const [uhr, setUhr] = useState(() => UHR(start))
  const [suche, setSuche] = useState('')
  const feld = useRef<HTMLInputElement>(null)
  useEffect(() => feld.current?.focus(), [])
  useEffect(() => {
    const taste = (e: KeyboardEvent) => e.key === 'Escape' && schliessen()
    window.addEventListener('keydown', taste)
    return () => window.removeEventListener('keydown', taste)
  }, [schliessen])

  const minuten = minutenAus(uhr)

  /**
   * **Ohne Suche die Gruppen, mit Suche eine Liste.** Jede Aufgabe einmal – wer in „Gefährdet"
   * steht, steht nicht noch einmal unter „Zum Einplanen".
   */
  const anzeige = useMemo((): EinplanenGruppe[] => {
    const q = suche.trim().toLowerCase()
    if (q === '') {
      const gesehen = new Set<string>()
      return gruppen
        .map((g) => ({
          titel: g.titel,
          zeilen: g.zeilen.filter((z) => !gesehen.has(z.id) && gesehen.add(z.id)),
        }))
        .filter((g) => g.zeilen.length > 0)
    }
    const gesehen = new Set<string>()
    const treffer = [...gruppen.flatMap((g) => g.zeilen), ...bestand].filter(
      (z) => z.title.toLowerCase().includes(q) && !gesehen.has(z.id) && gesehen.add(z.id),
    )
    return [
      {
        titel: `${treffer.length > 30 ? 'Die ersten 30' : treffer.length} Treffer`,
        zeilen: treffer.slice(0, 30),
      },
    ]
  }, [gruppen, bestand, suche])

  const waehlen = (id: string) => {
    if (minuten === null) return
    einplanen(id, minuten)
    schliessen()
  }

  return createPortal(
    <div
      className="unter-leiste fixed inset-x-0 bottom-0 z-[60] flex items-end justify-center bg-[rgb(23_22_20/0.28)] sm:items-center"
      role="dialog"
      aria-modal="true"
      aria-label={`Aufgabe einplanen – ${tagName}`}
      onClick={schliessen}
    >
      <div
        className="flex max-h-[min(80vh,100%)] w-full max-w-[480px] flex-col rounded-t-card border border-line bg-surface shadow-lg sm:rounded-card"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="flex items-center justify-between gap-3 border-b border-line px-4 py-3 text-[14.5px] font-bold">
          <span>Einplanen · {tagName}</span>
          <label className="flex items-center gap-1.5 text-[12.5px] font-normal text-ink2">
            <Clock className="size-3.5 text-sonne" aria-hidden />
            um
            <input
              type="time"
              step={900}
              value={uhr}
              onChange={(e) => setUhr(e.target.value)}
              aria-label="Uhrzeit"
              className="tabular rounded-lg border border-line2 px-2 py-1 text-[13px]"
            />
          </label>
        </h3>

        <div className="border-b border-line px-4 py-2.5">
          <label className="flex items-center gap-2 rounded-lg border border-line2 px-2.5 py-1.5 focus-within:border-accent">
            <Search className="size-3.5 text-muted" aria-hidden />
            <input
              ref={feld}
              value={suche}
              onChange={(e) => setSuche(e.target.value)}
              onKeyDown={(e) => {
                /** Enter nimmt den ersten Treffer – suchen, Enter, fertig. */
                const erste = anzeige[0]?.zeilen[0]
                if (e.key === 'Enter' && erste) waehlen(erste.id)
              }}
              placeholder="Aufgabe suchen …"
              aria-label="Aufgabe suchen"
              className="min-w-0 flex-1 bg-transparent text-[13px] outline-none"
            />
          </label>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto py-1">
          {anzeige.length === 0 || anzeige.every((g) => g.zeilen.length === 0) ? (
            <p className="px-4 py-5 text-center text-[12.5px] text-muted">
              {suche ? 'Nichts gefunden.' : 'Nichts offen.'}
            </p>
          ) : (
            anzeige.map((g) => (
              <section key={g.titel} className="py-1">
                <h4 className="px-4 pt-1.5 pb-1 text-[11px] font-semibold tracking-wide text-muted uppercase">
                  {g.titel}
                </h4>
                <ul>
                  {g.zeilen.map((z) => (
                    <li key={z.id}>
                      <button
                        type="button"
                        disabled={minuten === null}
                        onClick={() => waehlen(z.id)}
                        className={cn(
                          'flex w-full cursor-pointer items-baseline gap-2 px-4 py-1.5 text-left text-[13px] hover:bg-sonne-bg focus-visible:bg-sonne-bg focus-visible:outline-none',
                          minuten === null && 'cursor-not-allowed opacity-50',
                        )}
                      >
                        <span className="min-w-0 flex-1 truncate font-medium">{z.title}</span>
                        {z.hinweis && (
                          <span className="shrink-0 text-[11px] text-muted">{z.hinweis}</span>
                        )}
                      </button>
                    </li>
                  ))}
                </ul>
              </section>
            ))
          )}
        </div>

        <div className="flex items-center justify-between border-t border-line px-4 py-2.5 text-[11.5px] text-muted">
          <span>
            {minuten === null
              ? 'Erst eine Uhrzeit wählen.'
              : 'Die Aufgabe steht dann fest – auch über einem Termin.'}
          </span>
          <button
            type="button"
            onClick={schliessen}
            className="cursor-pointer rounded-md px-2 py-1 text-ink2 hover:bg-[#f3f2ef]"
          >
            Abbrechen
          </button>
        </div>
      </div>
    </div>,
    document.body,
  )
}
